import React, { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "@/lib/utils"

interface Feature {
  step: string
  title?: string
  content: string
  image: string
}

interface FeatureStepsProps {
  features: Feature[]
  className?: string
  title?: string
  autoPlayInterval?: number
  imageHeight?: string
}

export function FeatureSteps({
  features,
  className,
  title = "Everything You Need Included",
  autoPlayInterval = 3000,
  imageHeight = "h-[400px]",
}: FeatureStepsProps) {
  const [currentFeature, setCurrentFeature] = useState(0)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      if (progress < 100) {
        setProgress((prev) => prev + 100 / (autoPlayInterval / 100))
      } else {
        setCurrentFeature((prev) => (prev + 1) % features.length)
        setProgress(0)
      }
    }, 100)

    return () => clearInterval(timer)
  }, [progress, features.length, autoPlayInterval])

  return (
    <div className={cn("p-8 md:p-12", className)}>
      <div className="max-w-7xl mx-auto w-full">
        <h2
          className="text-3xl md:text-4xl lg:text-5xl mb-10 text-center"
          style={{ color: 'var(--color-cream)', fontFamily: 'var(--font-display)' }}
        >
          {title}
        </h2>

        <div className="flex flex-col md:grid md:grid-cols-2 gap-6 md:gap-10">
          <div className="order-2 md:order-1 space-y-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="flex items-center gap-6 md:gap-8"
                initial={{ opacity: 0.3 }}
                animate={{ opacity: index === currentFeature ? 1 : 0.3 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  className={cn(
                    "w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center border-2",
                  )}
                  style={
                    index === currentFeature
                      ? { backgroundColor: 'var(--color-red)', borderColor: 'var(--color-red)' }
                      : { backgroundColor: 'transparent', borderColor: 'var(--color-cream-border)' }
                  }
                >
                  {index <= currentFeature ? (
                    <span className="text-lg font-bold" style={{ color: 'var(--color-cream)' }}>✓</span>
                  ) : (
                    <span className="text-lg font-semibold" style={{ color: 'var(--color-cream-faint)' }}>{index + 1}</span>
                  )}
                </motion.div>

                <div className="flex-1">
                  <h3
                    className="text-xl md:text-2xl font-semibold"
                    style={{
                      color: index === currentFeature ? 'var(--color-cream)' : 'var(--color-cream-faint)',
                      fontFamily: 'var(--font-display)',
                    }}
                  >
                    {feature.title || feature.step}
                  </h3>
                  <p
                    className="text-sm md:text-lg"
                    style={{
                      color: index === currentFeature ? 'var(--color-cream-muted)' : 'var(--color-cream-faint)',
                      fontFamily: 'var(--font-body)',
                    }}
                  >
                    {feature.content}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <div
            className="order-1 md:order-2 relative h-[200px] md:h-[300px] lg:h-[400px] overflow-hidden rounded-lg"
            style={{ backgroundColor: 'var(--color-black)', border: '1px solid var(--color-cream-border)' }}
          >
            <AnimatePresence mode="wait">
              {features.map(
                (feature, index) =>
                  index === currentFeature && (
                    <motion.div
                      key={index}
                      className="absolute inset-0 rounded-lg overflow-hidden"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                    >
                      <img
                        src={feature.image}
                        alt={feature.step}
                        className="w-full h-full object-cover"
                      />
                    </motion.div>
                  ),
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  )
}
